// src/components/pages/CategoryPage.tsx

import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  MapPin,
  ArrowLeft,
  ChevronRight,
  Search,
  Building2,
} from 'lucide-react';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { GLASS_CARD } from '@/components/ui/glass';
import Footer from '@/components/pages/shared/Footer';
import { useCategoryCities } from '@/components/hooks/useCategoryCities';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const CARD_ACCENTS = [
  { bg: 'bg-[#103254]/8', icon: 'text-[#103254]' },
  { bg: 'bg-[#288E7F]/8', icon: 'text-[#288E7F]' },
  { bg: 'bg-indigo-50 dark:bg-indigo-900/20', icon: 'text-indigo-500' },
  { bg: 'bg-amber-50 dark:bg-amber-900/20', icon: 'text-amber-500' },
  { bg: 'bg-rose-50 dark:bg-rose-900/20', icon: 'text-rose-500' },
  { bg: 'bg-violet-50 dark:bg-violet-900/20', icon: 'text-violet-500' },
];

const CategoryPage = () => {
  const { categoria_slug = '' } = useParams<{ categoria_slug: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { ciudades, categoriaLabel, loading, error } =
    useCategoryCities(categoria_slug);

  const canonicalUrl = `https://bookoll.com/${categoria_slug}`;
  const pageTitle = `${categoriaLabel} en España — Reserva cita online | Bookoll`;
  const pageDescription = `Encuentra negocios de ${categoriaLabel.toLowerCase()} en distintas ciudades de España y reserva cita online fácilmente con Bookoll.`;

  const filtered = searchTerm.trim()
    ? ciudades.filter((c) =>
        c.ciudad.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : ciudades;

  const totalNegocios = ciudades.reduce((acc, c) => acc + c.total_empresas, 0);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${canonicalUrl}#webpage`,
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    inLanguage: 'es-ES',
    isPartOf: {
      '@type': 'WebSite',
      '@id': 'https://bookoll.com/#website',
      name: 'Bookoll',
      url: 'https://bookoll.com',
    },
    breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${canonicalUrl}#itemlist`,
    name: `${categoriaLabel} en España`,
    numberOfItems: filtered.length,
    itemListElement: filtered.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://bookoll.com/${categoria_slug}/${c.ciudad_slug}`,
      name: c.ciudad,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: 'https://bookoll.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoriaLabel,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-black">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content={`${categoriaLabel.toLowerCase()} España, ${categoriaLabel.toLowerCase()} reserva online, cita ${categoriaLabel.toLowerCase()}, negocios ${categoriaLabel.toLowerCase()}, Bookoll`}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Bookoll" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://bookoll.com/og-home.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={pageTitle} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bookoll" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://bookoll.com/og-home.jpg" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Header + Hero fusionados en un único gradiente continuo */}
      <div className="relative overflow-hidden text-white">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background:
              'linear-gradient(135deg, #103254 0%, #1a4a6e 50%, #288E7F 100%)',
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, igual que Login */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        <header className="relative sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border border-white/20 hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
            </button>
            <Link to="/" className="shrink-0">
              <img
                src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
                alt="Bookoll"
                className="h-7"
              />
            </Link>
            <nav
              aria-label="Breadcrumb"
              className="hidden sm:flex items-center gap-1.5 text-sm text-white/60 ml-2"
            >
              <Link to="/" className="hover:text-white transition-colors">
                Inicio
              </Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white font-semibold capitalize">
                {categoriaLabel}
              </span>
            </nav>
          </div>
        </header>

        <section className="relative py-16 px-4 sm:px-8">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 max-w-7xl mx-auto"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight capitalize">
              {categoriaLabel} <span className="text-white/70">en</span> España
            </h1>
            {!loading && ciudades.length > 0 && (
              <div className="flex flex-wrap gap-5 mt-4">
                <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base">
                  <MapPin className="w-4 h-4 text-white/60" />
                  <span>
                    <strong className="text-white font-semibold">
                      {ciudades.length}
                    </strong>{' '}
                    ciudad{ciudades.length !== 1 ? 'es' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base">
                  <Building2 className="w-4 h-4 text-white/60" />
                  <span>
                    <strong className="text-white font-semibold">
                      {totalNegocios}
                    </strong>{' '}
                    negocio{totalNegocios !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}
          </motion.div>
          <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
            <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-8">
              <path
                d="M0,20 C360,40 1080,0 1440,20 L1440,40 L0,40 Z"
                className="fill-white dark:fill-black"
              />
            </svg>
          </div>
        </section>
      </div>

      {/* Contenido */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-8 py-10">
        <div className="relative max-w-md mb-8">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
          <Input
            type="text"
            placeholder="Buscar ciudad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 rounded-xl border-gray-200 dark:border-white/10 bg-white/70 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#103254]/20"
          />
        </div>

        {loading && (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#103254] dark:border-t-white dark:border-b-white/20" />
          </div>
        )}
        {error && (
          <div className="text-center text-red-500 dark:text-red-400 mt-10">
            {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className={`text-center py-20 rounded-2xl ${GLASS_CARD} text-gray-400 dark:text-gray-600`}>
            <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-200 dark:text-gray-700" />
            <p className="text-base font-medium text-gray-500 dark:text-gray-400">
              No encontramos ciudades con {categoriaLabel.toLowerCase()}
            </p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {filtered.map((c, idx) => {
              const accent = CARD_ACCENTS[idx % CARD_ACCENTS.length];
              return (
                <motion.article
                  key={c.ciudad_slug}
                  variants={itemVariants}
                  className={`group rounded-2xl ${GLASS_CARD} hover:shadow-lg hover:border-[#103254]/20 dark:hover:border-white/20 transition-all duration-300 overflow-hidden cursor-pointer`}
                  onClick={() =>
                    navigate(`/${categoria_slug}/${c.ciudad_slug}`)
                  }
                >
                  <div
                    className="h-2 w-full"
                    style={{
                      background:
                        'linear-gradient(90deg, #103254 0%, #288E7F 100%)',
                      opacity: 0.7 + (idx % 3) * 0.1,
                    }}
                  />
                  <div className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div
                        className={`w-10 h-10 rounded-xl ${accent.bg} flex items-center justify-center shrink-0 mt-0.5`}
                      >
                        <MapPin className={`w-5 h-5 ${accent.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="font-semibold text-[#0f172a] dark:text-white text-base leading-tight capitalize truncate">
                          {c.ciudad}
                        </h2>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 capitalize">
                          {categoriaLabel}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-2">
                      <Building2 className="w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
                      <span>
                        <strong className="font-semibold text-[#0f172a] dark:text-white">
                          {c.total_empresas}
                        </strong>{' '}
                        negocio{c.total_empresas !== 1 ? 's' : ''} disponible
                        {c.total_empresas !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-white/10 flex items-center justify-between">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        Reserva cita online
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#103254] dark:text-[#4fb8a3] group-hover:gap-2 transition-all">
                        Ver negocios <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        )}

        {/* Sección SEO textual indexable */}
        {!loading && ciudades.length > 0 && (
          <section className="sr-only">
            <h2>
              {categoriaLabel} en España — Reserva cita online con Bookoll
            </h2>
            <p>
              Encuentra los mejores negocios de {categoriaLabel.toLowerCase()}{' '}
              en{' '}
              {ciudades
                .slice(0, 5)
                .map((c) => c.ciudad)
                .join(', ')}{' '}
              y más ciudades. Reserva cita online de forma fácil y rápida con
              Bookoll, disponible las 24 horas.
            </p>
            <ul>
              {ciudades.map((c) => (
                <li key={c.ciudad_slug}>
                  <Link to={`/${categoria_slug}/${c.ciudad_slug}`}>
                    {categoriaLabel} en {c.ciudad} — {c.total_empresas} negocio
                    {c.total_empresas !== 1 ? 's' : ''}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;